import React from 'react';
import parse from 'html-react-parser';

interface CalculatorProps {
	salary: number,
	procentage: number,
	isCalculating: boolean,
	doCalculation: (value: number, signal: AbortSignal)=>void,
	sliderOptions?: {
	min?: number,
	max?: number,
	step?: number,
	label?: string,
	body?: string,
	footer?: string,
	resultText1?: string,
	resultText2?: string,
	}
}


function useDebounce<T>(value: T, delay?: number): T {
	const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

	React.useEffect(() => {
		const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

		return () => {
			clearTimeout(timer);
		};
	}, [value, delay]);

	return debouncedValue;
}

const Calculator = ({ doCalculation, salary, procentage, sliderOptions = { min: 0, max: 8000, label: 'Beregn hvad en ekstra indbetaling vil betyde for dig', body: 'Vælg ekstra indbetaling om måneden', footer: 'Indenfor vores anbefaling på min. 70%', resultText1: 'kr./md. i alt udbetalt', resultText2: 'af din løn idag' } }:CalculatorProps) => {
	const [showAnimation, setShowAnimation] = React.useState(true);
	const [monthlyPayment, setMonthlyPayment] = React.useState<number>(0);
	const debouncedValue = useDebounce<number>(monthlyPayment, 200);

	const sliderRef = React.useRef<HTMLInputElement>(null);
	const sliderLabelRef = React.useRef<HTMLDivElement>(null);
	const sliderBgRef = React.useRef<HTMLDivElement>(null);


	const handleChange = (e: React.ChangeEvent<HTMLInputElement>)=>{
		const value = +e.target.value;
		setMonthlyPayment(value);
	};

	const handleMouseDown = ()=>{
		if (window) {
			window?.getSelection()?.removeAllRanges();
		}
	};


	React.useEffect(()=>{
		const controller = new AbortController();
		const { signal } = controller;

		if (!showAnimation) {
			doCalculation(debouncedValue, signal);
		}

		return ()=>{
			controller.abort();
		};
	}, [debouncedValue]);

	React.useEffect(()=>{
		if (sliderRef.current && sliderLabelRef.current){
			const sliderValuePct = +sliderRef.current.value / (+sliderRef.current.max - +sliderRef.current.min) * 100;
			sliderRef.current.style.background = `linear-gradient(to right, rgb(146, 0, 77) ${sliderValuePct}%, rgba(146, 0, 77, 0.3) ${sliderValuePct}%)`;
			sliderLabelRef.current.style.left = `${sliderValuePct > 88 ? 88 : sliderValuePct < 8 ? 8 : sliderValuePct}%`;
			sliderLabelRef.current.style.left = `${sliderValuePct > 88 ? 88 : sliderValuePct < 8 ? 8 : sliderValuePct}%`;

			if (monthlyPayment) {
				setShowAnimation(false);
			}
		}
	}, [monthlyPayment]);

	return (
		<div className={ 'lhp-calculator' }>
			<div className={ 'lhp-calculator__input' }>
				<div className="lhp-calculator__input__label">{ sliderOptions?.label || '' }</div>
				<div className="lhp-calculator__input__body">{ parse(sliderOptions?.body || '') }</div>
				<div className="slider-container">
					<input
						className="slider"
						type="range"
						min={ sliderOptions?.min || 0 }
						max={ sliderOptions?.max || 1000 }
						step={ sliderOptions?.step || 50 }
						value={ monthlyPayment }
						onMouseDown={ handleMouseDown }
						onChange={ handleChange }
						ref={ sliderRef }
					/>
					<div className="slider-label" ref={ sliderLabelRef }>{ monthlyPayment }<span>kr./md.</span></div>
					{ showAnimation && <div className="slider-bg" ref={ sliderBgRef } /> }
				</div>
			</div>
			<div className="lhp-calculator__result-section">
				<div className="lhp-calculator__result-section__result">
					<div className="lhp-calculator__result-section__result__primary">
						{ salary && salary.toLocaleString('da-DA') }
					</div>
					<div className="lhp-calculator__result-section__result__secondary">{ sliderOptions?.resultText1 }</div>
				</div>
				<div className="lhp-calculator__result-section__result">
					<div className="lhp-calculator__result-section__result__primary">
						{ Math.round(procentage) }%
					</div>
					<div className="lhp-calculator__result-section__result__secondary">{ sliderOptions?.resultText2 }</div>
				</div>
			</div>
		</div>
	);
};

export default Calculator;